import React, { useState } from 'react';
import { Tooltip, Popover } from 'antd';
import dayjs from 'dayjs';

// ── Capitalize Name Helper ───────────────────────────────────────────────────
const capitalizeName = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// ── Health badge ──────────────────────────────────────────────────────────────
const HealthBadge = ({ health }) => {
    const map = {
        'On Track': { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600', dot: 'bg-emerald-600' },
        'At Risk': { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-400' },
        'Critical': { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-600', dot: 'bg-red-500' },
    };
    const style = map[health] || map['On Track'];
    return (
        <span className={`flex items-center gap-1.5 text-xs font-bold ${style.text} ${style.bg} px-2.5 py-1 rounded-full`}>
            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
            {health}
        </span>
    );
};

// ── Status text ───────────────────────────────────────────────────────────────
const StatusText = ({ status }) => {
    const colorMap = {
        'Active': 'text-blue-600 dark:text-blue-400',
        'In Progress': 'text-blue-600 dark:text-blue-400',
        'On Hold': 'text-orange-600 dark:text-orange-400',
        'Completed': 'text-emerald-600 dark:text-emerald-400',
        'Closed': 'text-slate-600 dark:text-slate-400',
        'Close': 'text-slate-600 dark:text-slate-400',
        'Not Active': 'text-slate-500 dark:text-slate-400',
        'Inactive': 'text-slate-500 dark:text-slate-400',
        'Cancelled': 'text-red-500 dark:text-red-400',
        'Canceled': 'text-red-500 dark:text-red-400',
        'Draft': 'text-purple-600 dark:text-purple-400',
        'Backlog': 'text-slate-500 dark:text-slate-400',
        'Backlog / Upcoming': 'text-slate-500 dark:text-slate-400',
        'Archived': 'text-slate-400',
    };
    return <span className={`text-sm font-bold ${colorMap[status] || 'text-slate-500'}`}>{status || 'Draft'}</span>;
};

// ── Type badge ────────────────────────────────────────────────────────────────
const TypeBadge = ({ isClient }) => (
    <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${isClient
        ? 'bg-blue-100/60 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
        }`}>
        {isClient ? 'Client' : 'In-house'}
    </span>
);

// ── Avatar circle ─────────────────────────────────────────────────────────────
const bgColors = [
    'bg-amber-300', 'bg-emerald-400', 'bg-blue-600', 'bg-red-500',
    'bg-purple-500', 'bg-teal-400', 'bg-orange-400', 'bg-pink-400',
];

const AvatarCircle = ({ member, index = 0, small = false }) => {
    const name = capitalizeName(typeof member === 'object' ? (member.name ? `${member.name} ${member.surname || ''}`.trim() : '??') : (member || '??'));
    const role = member?.role || member?.designation || 'Collaborator';
    const department = member?.department || '—';
    const initials = name.split(' ').map(w => w[0]?.toUpperCase()).join('').slice(0, 2);
    const bg = bgColors[index % bgColors.length];
    const size = small ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-xs';
    const isLight = ['bg-amber-300', 'bg-emerald-400', 'bg-teal-400', 'bg-orange-400'].includes(bg);

    const popoverContent = (
        <div className="p-2 space-y-1.5 min-w-[160px]">
            <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center font-bold text-xs ${isLight ? 'text-slate-800' : 'text-white'}`}>
                    {initials}
                </div>
                <div>
                    <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">{name}</p>
                    <p className="text-[10px] text-slate-400 leading-tight">{role}</p>
                </div>
            </div>
            <div className="border-t border-slate-100 dark:border-slate-800 pt-1.5 flex justify-between text-[10px]">
                <span className="text-slate-400 font-medium">Dept:</span>
                <span className="font-bold text-slate-700 dark:text-slate-300">{department}</span>
            </div>
        </div>
    );

    return (
        <Popover content={popoverContent} trigger="click" placement="top">
            <Tooltip title={name}>
                <div className={`${size} rounded-full ${bg} flex items-center justify-center font-bold border-2 border-white dark:border-black cursor-pointer hover:scale-110 transition-transform ${isLight ? 'text-slate-800' : 'text-white'}`}>
                    {initials}
                </div>
            </Tooltip>
        </Popover>
    );
};

// ── CollaboratorsCell Component (Section 10 Spec) ─────────────────────────────
const CollaboratorsCell = ({ members = [], employees = [], ownerId = null }) => {
    const filteredMembers = (members || []).filter((m) => {
        const id = typeof m === 'object' && m !== null ? (m._id || m.id) : m;
        return !ownerId || String(id) !== String(ownerId);
    });

    if (!filteredMembers || filteredMembers.length === 0) {
        return <span className="text-xs text-slate-400 font-semibold">—</span>;
    }

    const resolvedMembers = filteredMembers.map((m) => {
        if (typeof m === 'object' && m !== null && (m.name || m.fullName)) {
            return {
                name: capitalizeName(m.fullName || `${m.name || ''} ${m.surname || ''}`.trim()),
                role: m.role || m.designation || 'Collaborator',
                department: m.department || 'Development',
            };
        }
        const emp = employees.find((e) => String(e._id) === String(m));
        return {
            name: capitalizeName(emp ? emp.name : (typeof m === 'string' ? m : 'Team Member')),
            role: emp?.role || emp?.designation || 'Collaborator',
            department: emp?.department || 'Development',
        };
    });

    const getInitials = (n) => n.split(' ').map((w) => w[0]?.toUpperCase()).join('').slice(0, 2);

    // ── Hover Tooltip Content (Avatars + Names) ──
    const hoverContent = (
        <div className="p-2 space-y-2 text-xs min-w-[200px]">
            <div className="flex items-center justify-between border-b border-slate-700 pb-1.5">
                <span className="font-bold text-white">Project Collaborators</span>
                <span className="bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{resolvedMembers.length}</span>
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {resolvedMembers.map((m, i) => {
                    const initials = getInitials(m.name);
                    const bg = bgColors[i % bgColors.length];
                    return (
                        <div key={i} className="flex items-center gap-2 text-slate-200">
                            <div className={`w-5 h-5 rounded-full ${bg} flex items-center justify-center font-bold text-[9px] text-white shrink-0`}>
                                {initials}
                            </div>
                            <span className="truncate font-medium">{m.name}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── Click Popover Content (Full Details: Avatar, Name, Role, Department) ──
    const clickPopoverContent = (
        <div className="p-3 space-y-3 min-w-[240px] max-w-[280px]">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                <span className="font-bold text-sm text-[#003263] dark:text-[#a7c8ff]">All Stakeholders ({resolvedMembers.length})</span>
            </div>
            <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                {resolvedMembers.map((m, i) => {
                    const initials = getInitials(m.name);
                    const bg = bgColors[i % bgColors.length];
                    return (
                        <div key={i} className="flex items-center gap-3 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-xl border border-slate-100 dark:border-slate-800">
                            <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center font-bold text-xs text-white shrink-0 shadow-sm`}>
                                {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{m.name}</p>
                                <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                                    <span className="truncate">{m.role}</span>
                                    <span>•</span>
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">{m.department}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <Popover content={clickPopoverContent} trigger="click" placement="top">
            <Tooltip title={hoverContent} placement="top">
                <div className="flex -space-x-2 cursor-pointer hover:opacity-90 transition-opacity items-center">
                    {resolvedMembers.slice(0, 3).map((m, i) => {
                        const initials = getInitials(m.name);
                        const bg = bgColors[i % bgColors.length];
                        return (
                            <div
                                key={i}
                                className={`w-7 h-7 rounded-full ${bg} flex items-center justify-center font-bold text-xs text-white border-2 border-white dark:border-black shadow-sm`}
                            >
                                {initials}
                            </div>
                        );
                    })}
                    {resolvedMembers.length > 3 && (
                        <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300 border-2 border-white dark:border-black shadow-sm">
                            +{resolvedMembers.length - 3}
                        </div>
                    )}
                </div>
            </Tooltip>
        </Popover>
    );
};

// ── Progress bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ percent }) => {
    const color = percent >= 80 ? 'bg-emerald-400' : percent >= 50 ? 'bg-blue-600' : percent >= 30 ? 'bg-orange-300' : 'bg-red-500';
    return (
        <div className="w-28">
            <div className="flex justify-between mb-1">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{percent}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${percent}%` }} />
            </div>
        </div>
    );
};

// ── Pagination ────────────────────────────────────────────────────────────────
const Pagination = ({ current, total, pageSize, onPageChange, onPageSizeChange }) => {
    const totalPages = Math.ceil(total / pageSize);
    const pages = [];

    if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
        pages.push(1);
        if (current > 3) pages.push('...');
        for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i++) pages.push(i);
        if (current < totalPages - 2) pages.push('...');
        pages.push(totalPages);
    }

    return (
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-black">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                Showing <span className="font-bold text-[#003263] dark:text-[#a7c8ff]">{Math.min((current - 1) * pageSize + 1, total)}-{Math.min(current * pageSize, total)}</span> of <span className="font-bold text-[#003263] dark:text-[#a7c8ff]">{total}</span> entries
            </div>
            <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Rows per page</label>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 pl-3 pr-8 py-1.5 rounded-lg text-xs font-bold text-[#003263] dark:text-[#a7c8ff] focus:ring-2 focus:ring-blue-400 transition-all outline-none appearance-none"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(current - 1)}
                        disabled={current <= 1}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all disabled:opacity-30"
                    >
                        <span className="material-symbols-outlined text-base">chevron_left</span>
                    </button>
                    {pages.map((p, i) =>
                        p === '...' ? (
                            <span key={`dots-${i}`} className="text-slate-400 px-1 text-xs">...</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => onPageChange(p)}
                                className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all ${p === current
                                    ? 'bg-[#003263] text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                {p}
                            </button>
                        )
                    )}
                    <button
                        onClick={() => onPageChange(current + 1)}
                        disabled={current >= totalPages}
                        className="p-1.5 rounded-lg text-[#003263] dark:text-[#a7c8ff] hover:bg-slate-100 dark:hover:bg-slate-800 transition-all disabled:opacity-30"
                    >
                        <span className="material-symbols-outlined text-base">chevron_right</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main table ────────────────────────────────────────────────────────────────
export default function ProjectListTable({
    projects,
    tasks,
    employees,
    loading,
    pagination,
    onPageChange,
    onPageSizeChange,
    onViewDetails,
    onEditProject,
}) {
    const [expandedMilestones, setExpandedMilestones] = useState({});
    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Compute per-project progress
    const getProjectProgress = (project) => {
        if (typeof project === 'object' && project !== null && project.progress !== undefined && project.progress !== null) {
            return project.progress;
        }
        const projectId = typeof project === 'object' && project !== null ? (project._id || project) : project;
        if (Array.isArray(tasks) && tasks.length > 0) {
            const projectTasks = tasks.filter(t => {
                const tid = t.project?._id || t.project;
                return String(tid) === String(projectId);
            });
            if (projectTasks.length > 0) {
                const completed = projectTasks.filter(t => t.status === 'Completed').length;
                return Math.round((completed / projectTasks.length) * 100);
            }
        }
        return (typeof project === 'object' && project?.progress !== undefined) ? project.progress : 0;
    };

    // Compute per-project milestones
    const getProjectMilestones = (project) => {
        const existingMilestones = (typeof project === 'object' && project?.milestones) ? project.milestones : [];
        if (!Array.isArray(tasks) || tasks.length === 0) return existingMilestones;
        const projectId = typeof project === 'object' && project !== null ? (project._id || project) : project;
        const projectTasks = tasks.filter(t => {
            const tid = t.project?._id || t.project;
            return String(tid) === String(projectId);
        });
        const taskMilestones = projectTasks.filter(t => t.itemType === 'milestone');
        const extraTaskMilestones = taskMilestones.filter(tm => 
            !existingMilestones.some(em => String(em._id || em.id) === String(tm._id) || (em.name || em.title) === tm.title)
        );
        return [...existingMilestones, ...extraTaskMilestones];
    };

    // Compute health based on stored field or progress + due date
    const getProjectHealth = (project) => {
        if (project.health) return project.health;
        const progress = getProjectProgress(project);
        if (progress === 100) return 'On Track';
        if (!project.dueDate) return 'On Track';
        const now = new Date();
        const due = new Date(project.dueDate);
        const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
        if (daysLeft < 0) return 'Critical';
        if (daysLeft < 7 && progress < 70) return 'At Risk';
        if (progress < 30 && daysLeft < 14) return 'At Risk';
        return 'On Track';
    };

    // Resolve member names from employees
    const getMemberNames = (memberIds = []) => {
        return memberIds.map(m => {
            const id = m?._id || m;
            const emp = employees.find(e => String(e._id) === String(id));
            return capitalizeName(emp?.name || m?.name || '??');
        });
    };

    // Get owner full name from createdBy or employee lookup
    const getOwnerName = (project) => {
        if (project.createdBy?.name) {
            return capitalizeName(`${project.createdBy.name} ${project.createdBy.surname || ''}`.trim());
        }
        const emp = employees.find(e => String(e._id) === String(project.createdBy));
        if (emp?.name) return capitalizeName(emp.name);
        if (project.supervisor) return capitalizeName(project.supervisor);
        return '—';
    };

    const getOwnerInitials = (project) => {
        const name = getOwnerName(project);
        if (name === '—') return '??';
        return name.split(' ').map(w => w[0]?.toUpperCase()).join('').slice(0, 2);
    };

    // Generate project code
    const getProjectCode = (project, index) => {
        return project.projectCode || `PRJ-${String(index + 1).padStart(3, '0')}`;
    };

    // Column Help Popovers
    const StatusHelp = (
        <div className="p-2 space-y-1.5 max-w-xs text-xs">
            <p className="font-bold text-slate-900 dark:text-white border-b pb-1">Project Status Guide</p>
            <p><span className="font-bold text-blue-600">In Progress:</span> Active execution under way.</p>
            <p><span className="font-bold text-amber-600">On Hold:</span> Paused pending feedback or scope update.</p>
            <p><span className="font-bold text-emerald-600">Completed:</span> Fully delivered and signed off.</p>
            <p><span className="font-bold text-slate-500">Backlog:</span> Planned project not yet started.</p>
        </div>
    );

    const HealthHelp = (
        <div className="p-2 space-y-1.5 max-w-xs text-xs">
            <p className="font-bold text-slate-900 dark:text-white border-b pb-1">Project Health Indicators</p>
            <p><span className="font-bold text-emerald-600">🟢 On Track:</span> Milestones & tasks progressing on schedule.</p>
            <p><span className="font-bold text-amber-600">🟡 At Risk:</span> Impending deadline or slight delay risk.</p>
            <p><span className="font-bold text-red-600">🔴 Critical:</span> Overdue tasks or critical blocker require immediate PM review.</p>
        </div>
    );

    const ProgressHelp = (
        <div className="p-2 space-y-1.5 max-w-xs text-xs">
            <p className="font-bold text-slate-900 dark:text-white border-b pb-1">Completion Progress Calculation</p>
            <p>Calculated automatically based on the ratio of completed tasks and approved milestones over total project deliverables:</p>
            <p className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800 p-1 rounded">Progress % = (Completed Tasks / Total Tasks) × 100</p>
        </div>
    );

    if (loading) {
        return (
            <div className="bg-white dark:bg-black rounded-2xl border border-slate-200 dark:border-slate-800 p-16 flex items-center justify-center">
                <div className="text-center space-y-3">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-xs font-bold text-slate-400">Loading projects...</p>
                </div>
            </div>
        );
    }

    if (projects.length === 0) {
        return (
            <div className="bg-white dark:bg-black rounded-2xl border border-slate-200 dark:border-slate-800 p-16 flex items-center justify-center">
                <div className="text-center space-y-3">
                    <span className="material-symbols-outlined text-4xl text-slate-300">folder_off</span>
                    <p className="text-sm font-bold text-slate-400">No projects found</p>
                    <p className="text-xs text-slate-400">Adjust your filters or create a new project.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-black rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" style={{ boxShadow: '0px 4px 12px rgba(25, 28, 29, 0.04)' }}>
            <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full min-w-[1200px] text-left border-separate" style={{ borderSpacing: 0 }}>
                    <thead>
                        <tr style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }} className="text-white">
                            <th 
                                className="px-5 py-4 text-sm font-black uppercase tracking-wider rounded-tl-xl" 
                                style={{ fontFamily: 'Manrope, sans-serif', position: 'sticky', left: 0, zIndex: 30, background: '#03488A', minWidth: '220px', width: '220px', maxWidth: '220px' }}
                            >
                                Project
                            </th>
                            <th 
                                className="px-4 py-4 text-sm font-black uppercase tracking-wider" 
                                style={{ fontFamily: 'Manrope, sans-serif', position: 'sticky', left: '220px', zIndex: 30, background: '#03488A', minWidth: '110px', width: '110px', maxWidth: '110px' }}
                            >
                                Type
                            </th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Category &amp; Sub-Cat</th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Duration</th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Owner</th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Collaborators</th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Milestones</th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <div className="flex items-center gap-1">
                                    <span>Status</span>
                                    <Popover content={StatusHelp} trigger="click" placement="top">
                                        <span className="material-symbols-outlined text-white/80 hover:text-white cursor-pointer select-none" style={{ fontSize: '14px' }}>help</span>
                                    </Popover>
                                </div>
                            </th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <div className="flex items-center gap-1">
                                    <span>Health</span>
                                    <Popover content={HealthHelp} trigger="click" placement="top">
                                        <span className="material-symbols-outlined text-white/80 hover:text-white cursor-pointer select-none" style={{ fontSize: '14px' }}>help</span>
                                    </Popover>
                                </div>
                            </th>
                            <th className="px-4 py-4 text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <div className="flex items-center gap-1">
                                    <span>Progress</span>
                                    <Popover content={ProgressHelp} trigger="click" placement="top">
                                        <span className="material-symbols-outlined text-white/80 hover:text-white cursor-pointer select-none" style={{ fontSize: '14px' }}>help</span>
                                    </Popover>
                                </div>
                            </th>
                            <th 
                                className="px-5 py-4 text-sm font-black uppercase tracking-wider rounded-tr-xl text-right" 
                                style={{ fontFamily: 'Manrope, sans-serif', position: 'sticky', right: 0, zIndex: 30, background: '#00AAF1', minWidth: '160px', width: '160px', maxWidth: '160px' }}
                            >
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-900">
                        {projects.map((project, idx) => {
                            const memberNames = getMemberNames(project.members);
                            const progress = getProjectProgress(project);
                            const health = getProjectHealth(project);
                            const category = project.category || project.projectType || 'General';
                            const subCategory = project.subCategory || project.projectSubType || '';
                            const isOngoing = project.durationMode === 'Ongoing' || (!project.dueDate && project.durationMode !== 'Fixed');
                            const isClient = Boolean(project.isClientProject || project.projectType === 'Client');
                            const questionnaireName = project.questionnaireTemplateName || (project.linkedQuestionnaire?.title || project.linkedQuestionnaire?.name || null);

                            return (
                                <tr
                                    key={project._id}
                                    className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors group text-sm"
                                >
                                    {/* Project Name + Code + Questionnaire indicator */}
                                    <td 
                                        className="px-5 py-4 bg-white dark:bg-black group-hover:bg-slate-50 dark:group-hover:bg-slate-900/50 border-r border-b border-slate-100 dark:border-slate-800 transition-colors duration-150"
                                        style={{ position: 'sticky', left: 0, zIndex: 20, minWidth: '220px', width: '220px', maxWidth: '220px' }}
                                    >
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-base font-extrabold text-[#003263] dark:text-[#a7c8ff]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                                    {project.name}
                                                </span>
                                                {(project.isDraft || project.status === 'Draft') && (
                                                    <span className="text-[11px] font-black px-2 py-0.5 bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 rounded-md">
                                                        DRAFT
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                                <span className="font-mono text-slate-500 font-bold">{getProjectCode(project, idx)}</span>
                                                {(project.brandName || project.brandId) && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-cyan-50 dark:bg-cyan-950/60 text-cyan-800 dark:text-cyan-200 border border-cyan-200 dark:border-cyan-800">
                                                        {project.brandName || (project.brandId?.includes('e360') ? 'e360' : project.brandId?.includes('cport') ? 'CPort' : 'Dymax')}
                                                    </span>
                                                )}
                                                {questionnaireName && (
                                                    <span className="text-blue-600 dark:text-blue-400 font-bold flex items-center gap-0.5 hover:underline cursor-pointer" onClick={() => onViewDetails?.(project)}>
                                                        📋 {questionnaireName.slice(0, 22)}… ↗
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Type */}
                                    <td 
                                        className="px-4 py-4 bg-white dark:bg-black group-hover:bg-slate-50 dark:group-hover:bg-slate-900/50 border-r border-b border-slate-100 dark:border-slate-800 transition-colors duration-150"
                                        style={{ position: 'sticky', left: '220px', zIndex: 20, minWidth: '110px', width: '110px', maxWidth: '110px' }}
                                    >
                                        <TypeBadge isClient={isClient} />
                                    </td>

                                    {/* Category & Sub-Category */}
                                    <td className="px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-sm text-slate-900 dark:text-slate-100">{category}</span>
                                            {subCategory && (
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                    ↳ {subCategory}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Duration Mode & Dates (Section 8) */}
                                    <td className="px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                                        {(() => {
                                            const start = dayjs(project.startDate || project.createdAt);
                                            const due = project.dueDate ? dayjs(project.dueDate) : null;
                                            const now = dayjs();

                                            if (isOngoing || !due) {
                                                return (
                                                    <Tooltip title="Ongoing Project: No predefined completion date; schedule performance is based on active task throughput.">
                                                        <div className="flex flex-col cursor-pointer">
                                                            <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                                                ● Ongoing
                                                            </span>
                                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                Started {formatDate(project.startDate || project.createdAt)}
                                                            </span>
                                                        </div>
                                                    </Tooltip>
                                                );
                                            }

                                            const totalDays = due.diff(start, 'day');
                                            const elapsedDays = Math.max(0, now.diff(start, 'day'));
                                            const remainingDays = due.diff(now, 'day');
                                            const isOverdue = remainingDays < 0 && progress < 100;

                                            const durationHelp = (
                                                <div className="p-2 space-y-1 text-xs">
                                                    <p className="font-bold text-white border-b border-slate-700 pb-1">Fixed Schedule Metrics</p>
                                                    <p>• Total Duration: <span className="font-bold text-blue-400">{totalDays} days</span></p>
                                                    <p>• Days Elapsed: <span className="font-bold text-slate-300">{elapsedDays} days</span></p>
                                                    <p>• Days Remaining: <span className={`font-bold ${remainingDays < 5 ? 'text-amber-400' : 'text-emerald-400'}`}>{remainingDays > 0 ? `${remainingDays} days` : '0 days (Due)'}</span></p>
                                                    <p>• Schedule Status: <span className={`font-bold ${isOverdue ? 'text-red-400' : 'text-emerald-400'}`}>{isOverdue ? 'Overdue' : 'On Schedule'}</span></p>
                                                </div>
                                            );

                                            return (
                                                <Tooltip title={durationHelp} placement="top">
                                                    <div className="flex flex-col cursor-pointer">
                                                        <span className="font-bold text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                                            Fixed
                                                        </span>
                                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                            {formatDate(start)} - {formatDate(due)}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            );
                                        })()}
                                    </td>

                                    {/* Owner */}
                                    <td className="px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-[#003263]/10 dark:bg-[#a7c8ff]/10 flex items-center justify-center text-xs font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                {getOwnerInitials(project)}
                                            </div>
                                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate max-w-[120px]">{getOwnerName(project)}</span>
                                        </div>
                                    </td>

                                    <td className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                        <CollaboratorsCell 
                                            members={project.members} 
                                            employees={employees} 
                                            ownerId={project.projectManager?._id || project.projectManager || project.createdBy?._id || project.createdBy}
                                        />
                                    </td>

                                    {/* Milestones */}
                                    <td className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                        {(() => {
                                            const milestones = getProjectMilestones(project);
                                            const completed = milestones.filter(m => m.status === 'Completed' || m.status === 'Closed' || m.status === 'Close').length;
                                            const total = milestones.length;

                                            if (total === 0) {
                                                return <span className="text-xs text-slate-400 font-semibold">—</span>;
                                            }

                                            const percentage = Math.round((completed / total) * 100);
                                            const badgeColor = percentage === 100 
                                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/60' 
                                                : percentage > 0
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-900/60'
                                                    : 'bg-slate-50 text-slate-600 dark:bg-slate-900/60 dark:text-slate-400 border border-slate-200 dark:border-slate-800';

                                            const isExpanded = !!expandedMilestones[project._id];
                                            const toggleExpanded = (e) => {
                                                e.stopPropagation();
                                                setExpandedMilestones(prev => ({
                                                    ...prev,
                                                    [project._id]: !prev[project._id]
                                                }));
                                            };

                                            return (
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                                                            {completed} / {total}
                                                        </span>
                                                        <button
                                                            onClick={toggleExpanded}
                                                            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer"
                                                            title={isExpanded ? "Hide Milestones" : "Show Milestones"}
                                                        >
                                                            <span className="material-symbols-outlined text-sm font-bold">
                                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="mt-1 space-y-1.5 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-100 dark:border-slate-800 max-w-[200px]">
                                                            {milestones.map((m, idx) => {
                                                                const mName = m.title || m.name || `Milestone ${idx + 1}`;
                                                                const mStatus = m.status || 'Pending';
                                                                const statusDots = {
                                                                    'Completed': '🟢',
                                                                    'Closed': '⚪',
                                                                    'Close': '⚪',
                                                                    'In Progress': '🔵',
                                                                    'In Escrow': '🔵',
                                                                    'Released': '🟢',
                                                                    'Delayed': '🔴',
                                                                    'Cancelled': '⚪',
                                                                    'Canceled': '⚪',
                                                                    'Pending': '⚪'
                                                                };
                                                                const dot = statusDots[mStatus] || '⚪';
                                                                return (
                                                                    <div key={idx} className="flex items-start gap-1 text-[11px] text-slate-700 dark:text-slate-300">
                                                                        <span className="shrink-0">{dot}</span>
                                                                        <div className="flex flex-col min-w-0">
                                                                            <span className="font-bold truncate" title={mName}>{mName}</span>
                                                                            <span className="text-[9px] opacity-75 font-semibold uppercase">{mStatus}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </td>

                                    {/* Status */}
                                    <td className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                        <StatusText status={project.status} />
                                    </td>

                                    {/* Health */}
                                    <td className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                        <HealthBadge health={health} />
                                    </td>

                                    {/* Progress */}
                                    <td className="px-4 py-3.5 border-b border-slate-100 dark:border-slate-800">
                                        <ProgressBar percent={progress} />
                                    </td>

                                    {/* Action */}
                                    <td 
                                        className="px-5 py-3.5 text-right bg-white dark:bg-black group-hover:bg-slate-50 dark:group-hover:bg-slate-900/50 border-l border-b border-slate-100 dark:border-slate-800 transition-colors duration-150"
                                        style={{ position: 'sticky', right: 0, zIndex: 20, minWidth: '160px', width: '160px', maxWidth: '160px' }}
                                    >
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onEditProject?.(project); }}
                                                className="text-amber-700 dark:text-amber-300 hover:text-amber-900 font-bold text-xs bg-amber-100 dark:bg-amber-950/60 px-2.5 py-1 rounded-lg transition-all shadow-sm"
                                            >
                                                {project.isDraft || project.status === 'Draft' ? 'Edit Draft' : 'Edit'}
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onViewDetails?.(project); }}
                                                className="text-[#003263] dark:text-[#a7c8ff] hover:text-blue-700 dark:hover:text-blue-300 font-bold text-xs transition-colors"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Pagination
                current={pagination.page}
                total={pagination.total}
                pageSize={pagination.pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
            />
        </div>
    );
}
